import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import authService from '../services/authService';
import bedrockService from '../services/bedrockService';
import Header from './Header';

function Chat() {
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showGuardrailModal, setShowGuardrailModal] = useState(false);
  const [pendingRetry, setPendingRetry] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [memoryRecords, setMemoryRecords] = useState([]);
  const [allLoadedConversations, setAllLoadedConversations] = useState([]);
  const [visibleUnpinnedCount, setVisibleUnpinnedCount] = useState(10);
  const [displayName, setDisplayName] = useState('');
  const messagesEndRef = useRef(null);
  const actorId = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Shared sort: pinned first (by pinOrder), then newest to oldest
  const sortConversations = (list) => {
    return [...list].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      if (a.pinned && b.pinned) return (a.pinOrder || 0) - (b.pinOrder || 0);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Redirect to login if token is expired (safety net in case route guard misses it)
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      window.location.href = '/';
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Auto-send message when a new conversation is created from the welcome screen
  useEffect(() => {
    if (currentConversationId && input.trim() && messages.length === 0 && !loading) {
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  // After initial load, expand visible conversations to 20
  useEffect(() => {
    if (!loadingHistory && allLoadedConversations.length > 0 && visibleUnpinnedCount === 5) {
      const timer = setTimeout(() => {
        const pinned = allLoadedConversations.filter(c => c.pinned);
        const unpinned = allLoadedConversations.filter(c => !c.pinned);
        const newCount = Math.min(20, unpinned.length);
        setVisibleUnpinnedCount(newCount);
        setConversations([...pinned, ...unpinned.slice(0, newCount)]);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingHistory, allLoadedConversations]);

  // Load user's allowed models
  useEffect(() => {
    const loadModels = async () => {
      try {
        console.log('Loading models from API...');
        const result = await bedrockService.getUserModels();
        console.log('Models API response:', result);
        
        if (result.models && result.models.length > 0) {
          console.log('Setting available models:', result.models);
          setAvailableModels(result.models);
          setSelectedModel(result.models[0].id);
        } else {
          console.error('No models available for user. Response:', result);
          alert('No models available. Check console for details.');
        }
      } catch (error) {
        console.error('Failed to load models:', error);
        if (error.message === 'Session expired') return;
        alert(`Failed to load models: ${error.message}`);
      } finally {
        setLoadingModels(false);
      }
    };

    loadModels();
  }, []);

  // Load all conversation sessions for the user
  useEffect(() => {
    const loadConversations = async () => {
      try {
        const userId = authService.getUserId();
        if (!userId) {
          console.error('No user ID found');
          setLoadingHistory(false);
          return;
        }

        actorId.current = userId;
        setDisplayName(authService.getDisplayName() || '');

        try {
          // Single API call — Lambda scans all sessions and returns metadata server-side
          const result = await bedrockService.listSessionsMetadata(userId);
          
          if (result.sessions && result.sessions.length > 0) {
            const allConvs = result.sessions.map(s => ({
              id: s.sessionId,
              messages: [],
              messagesLoaded: false,
              createdAt: s.createdAt,
              preview: s.preview || 'New conversation',
              pinned: s.pinned || false,
              pinOrder: s.pinOrder || 0,
              customName: s.customName || null,
              messageCount: s.messageCount || 0
            }));
            
            const sorted = sortConversations(allConvs);
            setAllLoadedConversations(sorted);
            
            const pinned = sorted.filter(c => c.pinned);
            const unpinned = sorted.filter(c => !c.pinned);
            setConversations([...pinned, ...unpinned.slice(0, 5)]);
            setVisibleUnpinnedCount(5);
            
            console.log(`Loaded ${sorted.length} conversations (${pinned.length} pinned, ${unpinned.length} unpinned)`);
          } else {
            console.log('No conversations found');
          }
        } catch (error) {
          console.error('Failed to load conversations:', error);
          if (error.message === 'Session expired') return;
        }
      } catch (error) {
        console.error('Failed to initialize conversations:', error);
        if (error.message === 'Session expired') return;
      } finally {
        setLoadingHistory(false);
      }
    };

    // Load memory records in parallel with conversations
    const loadMemoryRecords = async () => {
      try {
        const userId = authService.getUserId();
        if (!userId) return;
        console.log('Loading memory records for user:', userId);
        const memoryResult = await bedrockService.listMemoryRecords(userId);
        if (memoryResult.records && memoryResult.records.length > 0) {
          console.log(`Loaded ${memoryResult.records.length} memory records`);
          setMemoryRecords(memoryResult.records);
        } else {
          console.log('No memory records found for user');
        }
      } catch (memoryError) {
        console.error('Failed to load memory records:', memoryError);
      }
    };

    loadConversations();
    loadMemoryRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewConversation = async () => {
    const userId = authService.getUserId();
    const newSessionId = `user_${userId}_session_${Date.now()}`;
    const newConversation = {
      id: newSessionId,
      messages: [],
      messagesLoaded: true,
      createdAt: new Date().toISOString(),
      preview: 'New conversation',
      pinned: false
    };
    
    setAllLoadedConversations(prev => [newConversation, ...prev]);
    setConversations(prev => {
      const updated = [newConversation, ...prev];
      return sortConversations(updated);
    });
    setCurrentConversationId(newSessionId);
    setMessages([]);

    // Refresh memory records for the new conversation
    try {
      console.log('Refreshing memory records for new conversation...');
      const memoryResult = await bedrockService.listMemoryRecords(userId);
      if (memoryResult.records && memoryResult.records.length > 0) {
        console.log(`Loaded ${memoryResult.records.length} memory records for new conversation`);
        setMemoryRecords(memoryResult.records);
      } else {
        console.log('No memory records found');
      }
    } catch (err) {
      console.error('Failed to refresh memory records:', err);
    }
  };

  const handleLoadMoreSessions = () => {
    const pinned = allLoadedConversations.filter(c => c.pinned);
    const unpinned = allLoadedConversations.filter(c => !c.pinned);
    const newCount = Math.min(visibleUnpinnedCount + 10, unpinned.length);
    
    setVisibleUnpinnedCount(newCount);
    setConversations([...pinned, ...unpinned.slice(0, newCount)]);
  };

  const handleMovePinned = async (conversationId, direction) => {
    const pinned = sortConversations(allLoadedConversations).filter(c => c.pinned);
    const idx = pinned.findIndex(c => c.id === conversationId);
    if (idx < 0) return;
    
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= pinned.length) return;
    
    const currentOrder = pinned[idx].pinOrder || 0;
    const swapOrder = pinned[swapIdx].pinOrder || 0;
    
    try {
      await Promise.all([
        bedrockService.pinSession(actorId.current, conversationId, true, swapOrder),
        bedrockService.pinSession(actorId.current, pinned[swapIdx].id, true, currentOrder)
      ]);
      
      const updateOrders = (list) => {
        const updated = list.map(conv => {
          if (conv.id === conversationId) return { ...conv, pinOrder: swapOrder };
          if (conv.id === pinned[swapIdx].id) return { ...conv, pinOrder: currentOrder };
          return conv;
        });
        return sortConversations(updated);
      };
      
      setAllLoadedConversations(prev => updateOrders(prev));
      setConversations(prev => updateOrders(prev));
    } catch (error) {
      console.error('Failed to reorder pinned conversation:', error);
    }
  };

  const handleSelectConversation = async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;
    
    setCurrentConversationId(conversationId);
    
    if (conversation.messagesLoaded) {
      setMessages(conversation.messages);
      return;
    }
    
    setMessages([]);
    setLoading(true);
    try {
      const eventsResult = await bedrockService.listEvents(actorId.current, conversationId);
      const loadedMessages = [];
      
      if (eventsResult.events && eventsResult.events.length > 0) {
        const sortedEvents = eventsResult.events.sort((a, b) => new Date(a.eventTimestamp) - new Date(b.eventTimestamp));
        
        sortedEvents.forEach(event => {
          if (event.payload && Array.isArray(event.payload)) {
            event.payload.forEach(item => {
              if (item.conversational) {
                loadedMessages.push({
                  role: item.conversational.role.toLowerCase(),
                  content: [{ text: item.conversational.content.text }]
                });
              }
            });
          }
        });
      }
      
      setMessages(loadedMessages);
      
      const updateMessages = (list) => list.map(conv => 
        conv.id === conversationId ? { ...conv, messages: loadedMessages, messagesLoaded: true } : conv
      );
      setAllLoadedConversations(prev => updateMessages(prev));
      setConversations(prev => updateMessages(prev));
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePinConversation = async (conversationId, e) => {
    console.log('Pin button clicked!', conversationId);
    e.stopPropagation();
    e.preventDefault();
    
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) {
      console.log('Conversation not found');
      return;
    }
    
    const newPinnedState = !conversation.pinned;
    console.log('Toggling pin state from', conversation.pinned, 'to', newPinnedState);
    
    try {
      let newPinOrder = 0;
      if (newPinnedState) {
        const pinnedConvs = allLoadedConversations.filter(c => c.pinned);
        const maxOrder = pinnedConvs.reduce((max, c) => Math.max(max, c.pinOrder || 0), 0);
        newPinOrder = maxOrder + 1;
      }
      
      console.log('Calling pinSession API...');
      await bedrockService.pinSession(actorId.current, conversationId, newPinnedState, newPinOrder);
      console.log('Pin API call successful');
      
      const updatePin = (list) => {
        const updated = list.map(conv => conv.id === conversationId ? { ...conv, pinned: newPinnedState, pinOrder: newPinOrder } : conv);
        return sortConversations(updated);
      };
      setAllLoadedConversations(prev => updatePin(prev));
      setConversations(prev => updatePin(prev));
      console.log('Local state updated');
    } catch (error) {
      console.error('Failed to pin/unpin conversation:', error);
      alert(`Failed to pin conversation: ${error.message}`);
    }
  };

  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }
    
    setAllLoadedConversations(prev => prev.filter(conv => conv.id !== conversationId));
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    
    if (conversationId === currentConversationId) {
      setCurrentConversationId(null);
      setMessages([]);
    }
    
    try {
      await bedrockService.deleteSession(actorId.current, conversationId);
      console.log('Session deleted from AgentCore:', conversationId);
    } catch (error) {
      console.error('Failed to delete session from AgentCore:', error);
    }
  };

  const handleStartRename = (conversationId, currentName, e) => {
    e.stopPropagation();
    setEditingConversationId(conversationId);
    setEditingName(currentName || 'New conversation');
  };

  const handleFinishRename = async (conversationId) => {
    const trimmedName = editingName.trim();
    if (trimmedName) {
      const renameConv = (list) => list.map(conv => conv.id === conversationId ? { ...conv, customName: trimmedName } : conv);
      setAllLoadedConversations(prev => renameConv(prev));
      setConversations(prev => renameConv(prev));
      
      try {
        await bedrockService.addEvent(
          actorId.current,
          conversationId,
          [['Conversation renamed', 'OTHER']],
          {
            customName: { stringValue: trimmedName },
            action: { stringValue: 'rename' }
          }
        );
      } catch (err) {
        console.error('Failed to persist rename:', err);
      }
    }
    setEditingConversationId(null);
    setEditingName('');
  };

  const handleRenameKeyDown = (e, conversationId) => {
    if (e.key === 'Enter') {
      handleFinishRename(conversationId);
    } else if (e.key === 'Escape') {
      setEditingConversationId(null);
      setEditingName('');
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const supportedFormats = ['.pdf', '.html', '.docx', '.doc', '.md', '.txt'];
    const imageFormats = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
    
    const validFiles = [];
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (imageFormats.includes(ext)) {
        const bytes = await fileToBytes(file);
        const format = ext === '.jpeg' || ext === '.jpg' ? 'jpeg' : ext === '.gif' ? 'gif' : ext === '.webp' ? 'webp' : 'png';
        const previewUrl = URL.createObjectURL(file);
        validFiles.push({
          name: file.name,
          type: file.type,
          data: bytes,
          size: file.size,
          isImage: true,
          imageFormat: format,
          previewUrl: previewUrl
        });
      } else if (supportedFormats.includes(ext)) {
        const bytes = await fileToBytes(file);
        const sanitizedName = sanitizeFileName(file.name);
        validFiles.push({
          name: sanitizedName,
          type: file.type || getMimeType(ext),
          data: bytes,
          size: file.size
        });
      } else {
        alert(`File ${file.name} is not supported. Supported formats: PDF, HTML, Word, Markdown, Text, PNG, JPEG, GIF, WebP`);
      }
    }
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sanitizeFileName = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    // eslint-disable-next-line no-useless-escape
    let sanitized = name.replace(/[^a-zA-Z0-9\s\-\(\)\[\]]/g, '_');
    sanitized = sanitized.replace(/\s+/g, ' ');
    sanitized = sanitized.trim();
    if (!sanitized) {
      sanitized = 'document';
    }
    const finalName = sanitized;
    console.log('Original filename:', fileName);
    console.log('Sanitized filename (no extension):', finalName);
    return finalName;
  };

  const fileToBytes = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result);
        resolve(bytes);
      };
      reader.onerror = error => reject(error);
    });
  };

  const uint8ArrayToBase64 = (bytes) => {
    const CHUNK_SIZE = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      const chunk = bytes.subarray(i, Math.min(i + CHUNK_SIZE, bytes.length));
      binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
  };

  const getMimeType = (ext) => {
    const mimeTypes = {
      '.pdf': 'application/pdf',
      '.html': 'text/html',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.md': 'text/markdown',
      '.txt': 'text/plain'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        
        const bytes = await fileToBytes(file);
        const ext = file.type.split('/')[1] || 'png';
        const format = ext === 'jpeg' ? 'jpeg' : ext === 'jpg' ? 'jpeg' : ext === 'gif' ? 'gif' : ext === 'webp' ? 'webp' : 'png';
        
        // Create a thumbnail URL for preview
        const previewUrl = URL.createObjectURL(file);
        
        setAttachedFiles(prev => [...prev, {
          name: `pasted-image-${Date.now()}.${format}`,
          type: file.type,
          data: bytes,
          size: file.size,
          isImage: true,
          imageFormat: format,
          previewUrl: previewUrl
        }]);
      }
    }
  };

  const handleSend = async (skipGuardrail = false, retryText = null) => {
    const messageText = retryText || input;
    if ((!messageText.trim() && attachedFiles.length === 0) || loading) return;

    const content = [];
    if (messageText.trim()) {
      content.push({ text: messageText });
    }
    
    for (const file of attachedFiles) {
      if (file.isImage) {
        // Handle image attachments
        const base64 = uint8ArrayToBase64(file.data);
        content.push({
          image: {
            format: file.imageFormat,
            source: { bytes: base64 }
          }
        });
        console.log('Adding image to message:', { name: file.name, format: file.imageFormat, size: base64.length });
      } else {
        // Handle document attachments
        const ext = file.name.toLowerCase().split('.').pop();
        let format;
        if (ext === 'pdf') format = 'pdf';
        else if (ext === 'docx' || ext === 'doc') format = 'docx';
        else if (ext === 'html' || ext === 'htm') format = 'html';
        else if (ext === 'md') format = 'md';
        else if (ext === 'txt') format = 'txt';
        else format = 'txt';
        
        const base64 = uint8ArrayToBase64(file.data);
        content.push({
          document: {
            format: format,
            name: file.name,
            source: { bytes: base64 }
          }
        });
        console.log('Adding document to message:', { name: file.name, format: format, size: base64.length });
      }
    }

    const userMessage = { role: 'user', content: content };
    // Filter to only valid user/assistant messages with actual content (exclude empty placeholders)
    const validMessages = messages.filter(m => 
      (m.role === 'user' || m.role === 'assistant') && 
      m.content && m.content.some(c => (c.text && c.text.trim() !== '') || c.image || c.document)
    );
    const conversationHistory = retryText 
      ? [...validMessages]  // retryText means user message is already in validMessages
      : [...validMessages, userMessage];
    
    if (!retryText) {
      setMessages(prev => [...prev, userMessage]);
    }
    const userInput = messageText;
    setInput('');
    setAttachedFiles([]);
    setLoading(true);

    const assistantIndex = messages.length + (retryText ? 0 : 1);
    setMessages(prev => [...prev, { role: 'assistant', content: [{ text: '' }] }]);

    let fullText = '';
    let guardrailBlocked = false;

    try {
      // Build system context from memory records
      let systemContext = null;
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      let systemText = `Today's date is ${today}.`;
      
      if (memoryRecords.length > 0) {
        const memoryText = memoryRecords.map(record => {
          if (record.content && record.content.text) {
            return record.content.text;
          }
          return JSON.stringify(record.content || record);
        }).join('\n');
        
        systemText += `\n\nThe following are memories from previous conversations with this user. Use them to provide personalized responses:\n\n${memoryText}`;
      }
      
      systemContext = [{ text: systemText }];
      console.log('System context built with date and', memoryRecords.length, 'memory records');

      // Create abort controller for stop functionality
      abortControllerRef.current = new AbortController();

      await bedrockService.sendMessageStream(
        selectedModel,
        conversationHistory,
        (chunk) => {
          fullText += chunk;
          // Check for guardrail block as text streams in — don't show blocked text
          if (!skipGuardrail && (fullText.includes('sensitive information') || fullText.includes('I cannot process this request') || fullText.includes('機密情報') || fullText.includes('このリクエストには'))) {
            guardrailBlocked = true;
            // Keep showing "Thinking..." — don't update with blocked text
            return;
          }
          if (!guardrailBlocked) {
            setMessages(prev => {
              const updated = [...prev];
              updated[assistantIndex] = { role: 'assistant', content: [{ text: fullText }] };
              return updated;
            });
          }
        },
        skipGuardrail,
        systemContext,
        abortControllerRef.current.signal
      );

      // If guardrail blocked, show modal immediately — user still sees "Thinking..." state
      if (guardrailBlocked) {
        setPendingRetry(messageText);
        setShowGuardrailModal(true);
        setLoading(false);
        return;
      }

      const finalMessages = [...conversationHistory, { role: 'assistant', content: [{ text: fullText }] }];
      setMessages(finalMessages);

      const updateConvMessages = (prev) => {
        const updated = prev.map(conv => {
          if (conv.id === currentConversationId) {
            const firstUserMsg = finalMessages.find(m => m.role === 'user');
            const preview = firstUserMsg 
              ? firstUserMsg.content[0].text.substring(0, 50) + (firstUserMsg.content[0].text.length > 50 ? '...' : '')
              : 'New conversation';
            return { ...conv, messages: finalMessages, preview: preview };
          }
          return conv;
        });
        return sortConversations(updated);
      };
      setAllLoadedConversations(prev => updateConvMessages(prev));
      setConversations(prev => updateConvMessages(prev));

      try {
        await bedrockService.addEvent(
          actorId.current,
          currentConversationId,
          [
            [userInput, 'USER'],
            [fullText, 'ASSISTANT']
          ]
        );
      } catch (memoryError) {
        console.error('Failed to save conversation to memory:', memoryError);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User clicked stop — keep whatever text was generated so far
        if (fullText) {
          const stoppedMessages = [...conversationHistory, { role: 'assistant', content: [{ text: fullText }] }];
          setMessages(stoppedMessages);
        }
        console.log('Stream aborted by user');
      } else {
        console.error('Error:', error);
        setMessages(prev => {
          const updated = [...prev];
          updated[assistantIndex] = { 
            role: 'assistant', 
            content: [{ text: 'Error: Failed to get response. Please try again.' }] 
          };
          return updated;
        });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleGuardrailRetry = async () => {
    setShowGuardrailModal(false);
    const retryText = pendingRetry;
    setPendingRetry(null);
    // Remove the "Thinking..." assistant placeholder, keep user message, then re-send
    setMessages(prev => {
      // Remove last item (the empty assistant placeholder)
      return prev.slice(0, -1);
    });
    // Small delay to let state settle before re-sending
    await new Promise(r => setTimeout(r, 50));
    await handleSend(true, retryText);
  };

  const handleGuardrailCancel = () => {
    setShowGuardrailModal(false);
    setPendingRetry(null);
    // Remove both the user message and the "Thinking..." placeholder
    setMessages(prev => prev.slice(0, -2));
  };

  if (loadingHistory) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Header />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
            <span className="thinking-dot"></span>
            <span className="thinking-dot"></span>
            <span className="thinking-dot"></span>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Loading your conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      overflow: 'hidden',
      backgroundImage: 'url(/forester-background.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative'
    }}>
      {/* Semi-transparent overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.82)',
        zIndex: 0
      }} />
      
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Sidebar - Conversation History & Model Selection */}
        <div style={{ width: '320px', borderRight: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '20px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <h4 style={{ color: 'var(--subaru-blue)', marginBottom: '15px', fontSize: '1rem', fontWeight: '600' }}>SIA Secure AI Gateway</h4>
            <button 
              className="btn btn-primary w-100 mb-3" 
              onClick={handleNewConversation}
              disabled={loading}
            >
              + New Conversation
            </button>
            
            {/* Model Selection */}
            <div>
              <label style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '5px', display: 'block' }}>AI Model</label>
              <select 
                className="form-select form-select-sm" 
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={loading || loadingModels}
              >
                {availableModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.provider} - {model.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>
            <h6 style={{ padding: '6px', color: '#6c757d', fontSize: '0.7rem', textTransform: 'uppercase' }}>
              Conversations
            </h6>
            {conversations.map(conv => (
              <div
                key={conv.id}
                style={{
                  padding: '8px',
                  marginBottom: '3px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: conv.id === currentConversationId ? '#e7f3ff' : 'transparent',
                  border: conv.id === currentConversationId ? '1px solid #0d6efd' : '1px solid transparent',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (conv.id !== currentConversationId) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (conv.id !== currentConversationId) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div 
                    onClick={() => handleSelectConversation(conv.id)}
                    onDoubleClick={(e) => handleStartRename(conv.id, conv.customName || conv.preview, e)}
                    style={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }}
                  >
                    {editingConversationId === conv.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => handleFinishRename(conv.id)}
                        onKeyDown={(e) => handleRenameKeyDown(e, conv.id)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        style={{
                          fontSize: '0.8rem',
                          width: '100%',
                          border: '1px solid #0d6efd',
                          borderRadius: '3px',
                          padding: '2px 4px',
                          outline: 'none'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.8rem', fontWeight: conv.id === currentConversationId ? '600' : '400', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }}>
                        {conv.pinned && <span style={{ marginRight: '4px' }}>📌</span>}
                        {conv.customName || conv.preview || 'New conversation'}
                      </div>
                    )}
                  </div>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === conv.id ? null : conv.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        padding: '2px 6px',
                        opacity: openMenuId === conv.id ? 1 : 0.3,
                        transition: 'opacity 0.2s',
                        lineHeight: '1'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => { if (openMenuId !== conv.id) e.currentTarget.style.opacity = 0.3; }}
                      title="More options"
                    >
                      ⋮
                    </button>
                    {openMenuId === conv.id && (
                      <div style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 100,
                        minWidth: '140px',
                        overflow: 'hidden'
                      }}>
                        <div
                          onClick={(e) => { setOpenMenuId(null); handleStartRename(conv.id, conv.customName || conv.preview, e); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          ✏️ Rename
                        </div>
                        <div
                          onClick={(e) => { setOpenMenuId(null); handlePinConversation(conv.id, e); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          📌 {conv.pinned ? 'Unpin' : 'Pin'}
                        </div>
                        {conv.pinned && (() => {
                          const pinnedList = sortConversations(conversations).filter(c => c.pinned);
                          const pinnedIdx = pinnedList.findIndex(c => c.id === conv.id);
                          return (
                            <>
                              {pinnedIdx > 0 && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleMovePinned(conv.id, 'up'); }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                  ↑ Move up
                                </div>
                              )}
                              {pinnedIdx < pinnedList.length - 1 && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleMovePinned(conv.id, 'down'); }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                  ↓ Move down
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div
                          onClick={(e) => { setOpenMenuId(null); handleDeleteConversation(conv.id, e); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc3545' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          ✕ Delete
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {/* See more link */}
            {(() => {
              const unpinnedTotal = allLoadedConversations.filter(c => !c.pinned).length;
              return visibleUnpinnedCount < unpinnedTotal && (
                <div 
                  onClick={handleLoadMoreSessions}
                  style={{
                    padding: '8px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    color: '#0d6efd',
                    fontSize: '0.8rem'
                  }}
                >
                  See more ({visibleUnpinnedCount} of {unpinnedTotal})
                </div>
              );
            })()}
          </div>

          <div style={{ padding: '15px', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <button 
              onClick={() => authService.logout()}
              style={{
                width: '100%',
                background: 'none',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '10px',
                padding: '8px 0',
                fontSize: '0.85rem',
                color: '#6b7280',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Right - Chat Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Hidden file input — always in DOM so paperclip works on all screens */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.html,.docx,.doc,.md,.txt,.png,.jpg,.jpeg,.gif,.webp"
            multiple
            style={{ display: 'none' }}
          />
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {!currentConversationId ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                padding: '0 20px'
              }}>
                <h2 style={{ color: 'var(--subaru-blue)', marginBottom: '8px', fontWeight: '600', fontSize: '1.6rem' }}>
                  Welcome to the SIA Secure AI Gateway{displayName ? `, ${displayName}` : ''}.
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#9ca3af', marginBottom: '32px' }}>How can I help?</p>
                <div style={{ 
                  width: '100%',
                  maxWidth: '768px',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  border: '1px solid #d1d5db',
                  borderRadius: '16px',
                  padding: '8px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  {attachedFiles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      {attachedFiles.map((file, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', backgroundColor: '#f0f4ff', borderRadius: '8px', fontSize: '0.8rem', border: '1px solid #e0e7ff' }}>
                          {file.isImage && file.previewUrl ? (
                            <img src={file.previewUrl} alt="preview" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
                          ) : (<span>📎</span>)}
                          <span>{file.isImage ? 'Image' : file.name}</span>
                          <button onClick={() => removeAttachment(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '1rem', padding: '0 2px', lineHeight: '1' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      title="Attach files"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '4px', opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0 }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
                    >📎</button>
                    <textarea
                      placeholder="Type your message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (input.trim() || attachedFiles.length > 0) {
                            handleNewConversation();
                          }
                        }
                      }}
                      rows={1}
                      style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', overflow: 'hidden', minHeight: '24px', maxHeight: '150px', fontSize: '0.95rem', lineHeight: '1.5', padding: '2px 0', backgroundColor: 'transparent', fontFamily: 'inherit' }}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'; }}
                    />
                    <button 
                      onClick={() => { if (input.trim() || attachedFiles.length > 0) handleNewConversation(); }}
                      disabled={loading || (!input.trim() && attachedFiles.length === 0)}
                      style={{
                        backgroundColor: loading || (!input.trim() && attachedFiles.length === 0) ? '#d1d5db' : 'var(--subaru-blue)',
                        color: 'white', border: 'none', borderRadius: '10px', padding: '6px 16px', fontSize: '0.85rem', fontWeight: '500',
                        cursor: loading || (!input.trim() && attachedFiles.length === 0) ? 'default' : 'pointer', transition: 'background-color 0.2s', flexShrink: 0
                      }}
                    >Send</button>
                  </div>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                padding: '0 20px'
              }}>
                <p style={{ fontSize: '1rem', color: '#9ca3af', marginBottom: '24px' }}>How can I help{displayName ? `, ${displayName}` : ''}?</p>
                <div style={{ 
                  width: '100%',
                  maxWidth: '768px',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  border: '1px solid #d1d5db',
                  borderRadius: '16px',
                  padding: '8px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  {attachedFiles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      {attachedFiles.map((file, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', backgroundColor: '#f0f4ff', borderRadius: '8px', fontSize: '0.8rem', border: '1px solid #e0e7ff' }}>
                          {file.isImage && file.previewUrl ? (
                            <img src={file.previewUrl} alt="preview" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
                          ) : (<span>📎</span>)}
                          <span>{file.isImage ? 'Image' : file.name}</span>
                          <button onClick={() => removeAttachment(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '1rem', padding: '0 2px', lineHeight: '1' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      title="Attach files"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '4px', opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0 }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
                    >📎</button>
                    <textarea
                      placeholder="Type your message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      rows={1}
                      style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', overflow: 'hidden', minHeight: '24px', maxHeight: '150px', fontSize: '0.95rem', lineHeight: '1.5', padding: '2px 0', backgroundColor: 'transparent', fontFamily: 'inherit' }}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'; }}
                    />
                    <button 
                      onClick={handleSend}
                      disabled={loading || (!input.trim() && attachedFiles.length === 0)}
                      style={{
                        backgroundColor: loading || (!input.trim() && attachedFiles.length === 0) ? '#d1d5db' : 'var(--subaru-blue)',
                        color: 'white', border: 'none', borderRadius: '10px', padding: '6px 16px', fontSize: '0.85rem', fontWeight: '500',
                        cursor: loading || (!input.trim() && attachedFiles.length === 0) ? 'default' : 'pointer', transition: 'background-color 0.2s', flexShrink: 0
                      }}
                    >Send</button>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} style={{
                  padding: '24px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ maxWidth: '768px', margin: '0 auto', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: msg.role === 'user' ? '8px' : '50%',
                      backgroundColor: msg.role === 'user' ? 'var(--subaru-blue)' : 'transparent',
                      color: msg.role === 'user' ? 'white' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      {msg.role === 'user' ? 'You' : (
                        <img src="/subaru-logo.svg" alt="SIA" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                        {msg.role === 'user' ? 'You' : 'SIA AI Gateway'}
                      </div>
                      <div style={{ color: '#374151', lineHeight: '1.7', fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.role === 'assistant' && msg.content.every(item => !item.text && !item.document) ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                            <span style={{ fontSize: '0.85rem', color: '#9ca3af', fontWeight: '500' }}>Thinking</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <span className="thinking-dot"></span>
                              <span className="thinking-dot"></span>
                              <span className="thinking-dot"></span>
                            </div>
                          </div>
                        ) : (
                        msg.content.map((item, contentIdx) => (
                          <span key={contentIdx}>
                            {item.text && msg.role === 'assistant' ? (
                              <ReactMarkdown
                                components={{
                                  p: ({children}) => <p style={{ margin: '0 0 12px 0' }}>{children}</p>,
                                  code: ({inline, children, ...props}) => 
                                    inline ? (
                                      <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '0.88em', fontFamily: "'Consolas', 'Monaco', monospace" }} {...props}>{children}</code>
                                    ) : (
                                      <pre style={{ backgroundColor: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '0.88em', fontFamily: "'Consolas', 'Monaco', monospace", margin: '12px 0' }}>
                                        <code {...props}>{children}</code>
                                      </pre>
                                    ),
                                  ul: ({children}) => <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ul>,
                                  ol: ({children}) => <ol style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ol>,
                                  li: ({children}) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                                  strong: ({children}) => <strong style={{ fontWeight: '600' }}>{children}</strong>,
                                  h1: ({children}) => <h1 style={{ fontSize: '1.4em', fontWeight: '600', margin: '16px 0 8px' }}>{children}</h1>,
                                  h2: ({children}) => <h2 style={{ fontSize: '1.2em', fontWeight: '600', margin: '14px 0 6px' }}>{children}</h2>,
                                  h3: ({children}) => <h3 style={{ fontSize: '1.1em', fontWeight: '600', margin: '12px 0 4px' }}>{children}</h3>,
                                  blockquote: ({children}) => <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: '12px', margin: '8px 0', color: '#6b7280' }}>{children}</blockquote>,
                                }}
                              >{item.text}</ReactMarkdown>
                            ) : item.text ? (
                              <span>{item.text}</span>
                            ) : null}
                            {item.document && (
                              <div style={{ 
                                marginTop: '8px',
                                padding: '8px 12px',
                                backgroundColor: '#f7f7f8',
                                borderRadius: '8px',
                                border: '1px solid #e5e5e5',
                                fontSize: '0.85rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                📎 {item.document.name}
                              </div>
                            )}
                            {item.image && item.image.source && (
                              <div style={{ marginTop: '8px' }}>
                                <img 
                                  src={`data:image/${item.image.format || 'png'};base64,${item.image.source.bytes}`}
                                  alt="Attached"
                                  style={{ maxWidth: '400px', maxHeight: '300px', borderRadius: '8px', border: '1px solid #e5e5e5' }}
                                />
                              </div>
                            )}
                          </span>
                        ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {currentConversationId && messages.length > 0 && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ 
                maxWidth: '768px', 
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: '1px solid #d1d5db',
                borderRadius: '16px',
                padding: '8px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
              }}>
                {/* Attached Files Display — inside the input bar */}
                {attachedFiles.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    {attachedFiles.map((file, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '3px 8px',
                        backgroundColor: '#f0f4ff',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        border: '1px solid #e0e7ff'
                      }}>
                        {file.isImage && file.previewUrl ? (
                          <img src={file.previewUrl} alt="preview" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
                        ) : (
                          <span>📎</span>
                        )}
                        <span>{file.isImage ? 'Image' : file.name}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '1rem', padding: '0 2px', lineHeight: '1' }}
                          title="Remove"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    title="Attach files"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '4px', opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0 }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
                  >📎</button>
                  <textarea
                    ref={textareaRef}
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPaste={handlePaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={1}
                    style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', overflow: 'hidden', minHeight: '24px', maxHeight: '150px', fontSize: '0.95rem', lineHeight: '1.5', padding: '2px 0', backgroundColor: 'transparent', fontFamily: 'inherit' }}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'; }}
                  />
                  <button 
                    onClick={loading ? handleStop : handleSend}
                    disabled={!loading && (!input.trim() && attachedFiles.length === 0)}
                    style={{
                      backgroundColor: loading ? '#dc3545' : (!input.trim() && attachedFiles.length === 0) ? '#d1d5db' : 'var(--subaru-blue)',
                      color: 'white', border: 'none', borderRadius: '10px', padding: '6px 16px', fontSize: '0.85rem', fontWeight: '500',
                      cursor: (!loading && !input.trim() && attachedFiles.length === 0) ? 'default' : 'pointer', transition: 'background-color 0.2s', flexShrink: 0
                    }}
                  >{loading ? 'Stop' : 'Send'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Guardrail Modal */}
      {showGuardrailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
          }}>
            <h4 style={{ color: 'var(--subaru-blue)', marginBottom: '16px', fontWeight: '600', fontSize: '1.1rem' }}>Content Blocked</h4>
            <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#374151', fontSize: '0.9rem' }}>
              This request was blocked because it may include sensitive, confidential, or personal information that is not permitted for use with AI systems under Subaru of Indiana Automotive's legal policies.
            </p>
            <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#374151', fontSize: '0.9rem' }}>
              To protect SIA, its associates, and our partners, AI interactions are monitored and restricted when potentially sensitive data is detected.
            </p>
            <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#374151', fontSize: '0.9rem', fontWeight: '500' }}>
              Only proceed if you are certain this request contains no sensitive, confidential, or personal information and you are authorized to bypass these safeguards. Responsibility for appropriate AI use remains with the user.
            </p>
            <p style={{ marginBottom: '24px', lineHeight: '1.6', color: '#374151', fontSize: '0.9rem' }}>
              Would you like to continue your conversation?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleGuardrailCancel}
                style={{
                  background: 'none',
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  padding: '8px 24px',
                  fontSize: '0.85rem',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s'
                }}
              >
                No
              </button>
              <button 
                onClick={handleGuardrailRetry}
                style={{
                  backgroundColor: 'var(--subaru-blue)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px 24px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background-color 0.2s'
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Chat;
